<script lang="ts">
  import { untrack } from 'svelte';
  import { app } from './appState.svelte';
  import Toolbar from './Toolbar.svelte';
  import SelectionBar from './SelectionBar.svelte';
  import TextBoxEditor from './TextBoxEditor.svelte';
  import HistoryBar from './HistoryBar.svelte';
  import NewPageDialog from './NewPageDialog.svelte';
  import PagesDrawer from './PagesDrawer.svelte';
  import DebugOverlay from './DebugOverlay.svelte';
  import { InkEngine, type Tool } from '../ink/engine';
  import type { SearchBox } from '../sync/sync';
  import type { RemotePurge } from '../store/repo';
  import type { Page, Notebook, PageKind, TemplateId, TextBox } from '../store/types';

  let { notebookId, pageId }: { notebookId: string; pageId: string } = $props();

  const debug = new URLSearchParams(location.search).has('debug');

  let host: HTMLDivElement;
  let engine = $state<InkEngine | null>(null);
  let page = $state<Page | null>(null);
  let notebook = $state<Notebook | null>(null);
  let writing = $state(false);
  let canUndo = $state(false);
  let canRedo = $state(false);
  let showNewPage = $state(false);
  let showPages = $state(false);
  /** Page records from notebook.pageOrder that are not tombstoned, in order. */
  let livePageOrder = $state<Page[]>([]);
  let pageIndex = $derived(Math.max(0, livePageOrder.findIndex((p) => p.id === pageId)));

  // ---- text overlay editor (M3b spec §1.3) ----
  /** Clone of the box being edited; non-null mounts TextBoxEditor. */
  let textEdit = $state<TextBox | null>(null);
  let textEditorRef = $state<{ save(): Promise<void> } | null>(null);

  // ---- version history (M3b spec §3) ----
  let historyMode = $state(false);
  /** ⏱ is disabled on empty pages (no stroke events to scrub). */
  let historyEnabled = $state(false);

  // ---- files (M4): cached authed read of an immutable R2 object ----
  // Local-first: repo.getFile serves from the IDB cache; only files first
  // seen on another device hit the fetcher. The sync engine is read at CALL
  // time (not capture time) so enabling sync mid-session starts working
  // without a remount; with sync disabled the fetcher throws a clear error
  // and the bitmap/underlay for that file simply never renders.
  const getFileBytes = (id: string) =>
    app.repo!.getFile(id, (fid) => {
      const sync = app.syncEngine;
      if (!sync) throw new Error('sync disabled: file is not available on this device');
      return sync.fetchFile(fid);
    });

  $effect(() => {
    // Read the reactive props synchronously (before any `await`) so this effect
    // re-runs whenever navigation changes either id. Reads that happen inside an
    // async function's post-`await` continuation run as a microtask *after* this
    // effect callback has already returned, outside Svelte's dependency-tracking
    // window, and would silently fail to be tracked.
    const nb = notebookId;
    const pg = pageId;
    // Reset per-page toolbar state so it can't leak across page navigation
    // (history is per-engine; a new page starts with nothing to undo).
    writing = false;
    canUndo = false;
    canRedo = false;
    pendingRemoteReload = false;
    textEdit = null; // nav paths saved it via closeTextEditor() before routing
    historyMode = false;
    historyEnabled = false;
    let cancelled = false;
    let localEngine: InkEngine | null = null;
    (async () => {
      const nbRec = (await app.repo!.getNotebook(nb)) ?? null;
      notebook = nbRec;
      const pgRec = (await app.repo!.getPage(pg)) ?? null;
      page = pgRec;
      // Filter tombstoned pages out of the navigation order.
      const order: Page[] = [];
      for (const id of nbRec?.pageOrder ?? []) {
        const rec = id === pg ? pgRec : await app.repo!.getPage(id);
        if (rec && !rec.deletedAt) order.push(rec);
      }
      if (cancelled) return;
      livePageOrder = order;
      if (!pgRec || pgRec.deletedAt) {
        // Page is gone (possibly tombstoned remotely) — fall back to the
        // first live page, or the library if none remain.
        const fallback = order.find((p) => p.id !== pg);
        app.route = fallback
          ? { screen: 'editor', notebookId: nb, pageId: fallback.id }
          : { screen: 'library' };
        return;
      }
      localEngine = await InkEngine.create(host, pgRec, app.repo!, { getFileBytes });
      if (cancelled) { localEngine.destroy(); return; }
      const eng = localEngine;
      eng.on('strokeactive', (a) => (writing = a));
      eng.on('historychange', (h) => { canUndo = h.canUndo; canRedo = h.canRedo; });
      eng.on('error', (m) => app.showToast(`Couldn't save: ${m}`));
      eng.on('textboxtap', (tap) => void handleTextTap(eng, tap));
      // ⏱ availability: any stroke event ever (incl. tombstoned) enables it.
      // historychange fires before persistence — settle via idle() first.
      eng.on('historychange', () => {
        void eng.idle().then(() => { if (!cancelled) void refreshHistoryEnabled(pg); });
      });
      void refreshHistoryEnabled(pg);
      if (debug) {
        // Smoke-test hook (?debug only): after each history change, expose the
        // newest committed stroke's point count on window.__cote_debug.
        eng.on('historychange', () => {
          void (async () => {
            await eng.idle(); // historychange can fire before the stroke persists
            const strokes = await app.repo!.getActiveStrokes(pg);
            const last = strokes[strokes.length - 1];
            (window as any).__cote_debug = { lastCommittedPointCount: last ? last.points.length / 4 : 0 };
          })();
        });
      }
      const settings = await app.repo!.getSettings();
      localEngine.setSingleFingerDraws(settings.singleFingerDraws);
      localEngine.setTheme(app.theme);
      if (cancelled) return; // cleanup already destroyed localEngine; don't publish it
      engine = localEngine;
    })();
    return () => { cancelled = true; void localEngine?.persistViewport(); localEngine?.destroy(); engine = null; };
  });

  $effect(() => { engine?.setTheme(app.theme); });

  async function refreshHistoryEnabled(pg: string) {
    const has = (await app.repo!.getAllStrokes(pg)).length > 0;
    if (pg === pageId) historyEnabled = has; // ignore stale resolutions after nav
  }

  // ---- live refresh from sync (spec §3.2): reload strokes + text boxes when
  // a pull changed this page. Never mid-stroke — defer to pen-up instead.
  let pendingRemoteReload = $state(false);
  $effect(() => {
    const sync = app.syncEngine;
    const pg = pageId;
    if (!sync) return;
    const onPageChanged = (ids: Set<string>) => {
      if (!ids.has(pg)) return;
      // `writing`/`engine` read at event time (untracked), by design.
      if (writing || !engine) pendingRemoteReload = true;
      else reloadFromRemote(engine, pg);
    };
    sync.on('pagechanged', onPageChanged);
    return () => sync.off('pagechanged', onPageChanged);
  });
  // Deferred reload fires on pen-up (writing -> false) or once the engine exists.
  $effect(() => {
    if (pendingRemoteReload && !writing && engine) {
      pendingRemoteReload = false;
      reloadFromRemote(engine, pageId);
    }
  });
  function reloadFromRemote(eng: InkEngine, pg: string) {
    // No historyMode deferral needed here: during history preview the engine
    // itself skips the stroke swap (reloadStrokes early-returns) and re-reads
    // the repo fresh on exitHistoryPreview/restoreToTime, so remote changes
    // land the moment the preview closes. reloadTextBoxes is preview-safe
    // (text layer only). Deferral is only for `writing` (mid-stroke).
    void eng.reloadStrokes();
    void eng.reloadTextBoxes();
    void eng.reloadImages();
    void refreshHistoryEnabled(pg);
  }

  // ---- remote purge bail-out (M3b spec §2): the notebook/page we're showing
  // ceased to exist on another device. The event fires once per purge row and
  // can repeat across pulls (server re-sends / self-echoes) — `bailed` makes
  // the route-away + toast fire at most once per mounted page.
  $effect(() => {
    const sync = app.syncEngine;
    const nb = notebookId;
    const pg = pageId;
    if (!sync) return;
    let bailed = false;
    const onPurged = (purge: RemotePurge) => {
      if (bailed) return;
      const pageHit = purge.target === 'page' && purge.id === pg;
      const notebookHit = purge.target === 'notebook' && purge.id === nb;
      if (!pageHit && !notebookHit) return;
      bailed = true;
      app.showToast(pageHit ? 'This page was deleted on another device' : 'This notebook was deleted on another device');
      app.route = { screen: 'library' };
    };
    sync.on('purged', onPurged);
    return () => sync.off('purged', onPurged);
  });

  // ---- search match highlights (M5 spec §3.2): consume the one-shot
  // pendingSearchHighlight when this page's engine mounts, draw accent-tinted
  // divs at world→screen rects (repositioned on viewport events, SelectionBar
  // pattern), fade after 4s, clear on first pen-down. pendingSearchHighlight
  // is read UNTRACKED: consuming (nulling) it inside a tracking effect would
  // re-run the effect and cancel the fade timers via cleanup.
  let searchHighlight = $state<SearchBox[] | null>(null);
  let hlFading = $state(false);
  let hlVp = $state<{ x: number; y: number; scale: number }>({ x: 0, y: 0, scale: 1 });
  $effect(() => {
    const eng = engine;
    const pg = pageId;
    if (!eng) return;
    const pending = untrack(() => app.pendingSearchHighlight);
    if (!pending || pending.pageId !== pg) return;
    app.pendingSearchHighlight = null; // one-shot: consumed
    searchHighlight = pending.boxes;
    hlFading = false;
    hlVp = eng.getViewport();
    // No off() on the engine: stale listeners die with the engine (Editor
    // destroys it on nav), and the guards below make them inert meanwhile.
    eng.on('viewport', (v) => { if (eng === engine) hlVp = v; });
    eng.on('strokeactive', (a) => { if (a && eng === engine) searchHighlight = null; });
    const fade = setTimeout(() => (hlFading = true), 4000);
    const clear = setTimeout(() => (searchHighlight = null), 4800);
    return () => { clearTimeout(fade); clearTimeout(clear); searchHighlight = null; };
  });

  // ---- text overlay editor ----

  /** Save (if open) and unmount the overlay. Safe to call when closed. */
  async function closeTextEditor() {
    const ref = textEditorRef;
    textEdit = null;
    if (ref) await ref.save();
  }

  async function handleTextTap(eng: InkEngine, tap: { world: { x: number; y: number }; boxId: string | null }) {
    await closeTextEditor(); // an open editor saves before the next one opens
    if (eng !== engine) return; // page navigated mid-await
    if (tap.boxId) {
      const existing = eng.getTextBoxes().find((b) => b.id === tap.boxId);
      if (existing) textEdit = { ...existing };
    } else {
      const created = await eng.createTextBoxAt(tap.world);
      if (eng !== engine) return;
      textEdit = { ...created };
    }
  }

  function onToolChange(t: Tool) {
    if (t !== 'text') void closeTextEditor();
  }

  async function openHistory() {
    if (!engine || historyMode) return;
    await closeTextEditor();
    historyMode = true; // HistoryBar enters preview on mount
  }

  async function goToPage(idx: number) {
    const target = livePageOrder[idx]?.id;
    if (!target || target === pageId) return;
    await closeTextEditor();
    await engine?.commitSelection();
    await engine?.idle();
    app.route = { screen: 'editor', notebookId, pageId: target };
  }

  async function pickPage(target: string) {
    if (target !== pageId) {
      await closeTextEditor();
      await engine?.commitSelection();
      await engine?.idle();
      app.route = { screen: 'editor', notebookId, pageId: target };
    }
    showPages = false;
  }

  async function createPage(kind: PageKind, templateId: TemplateId) {
    const p = await app.repo!.createPage(notebookId, { kind, templateId, afterPageId: pageId });
    showNewPage = false;
    await closeTextEditor();
    await engine?.commitSelection();
    await engine?.idle();
    app.route = { screen: 'editor', notebookId, pageId: p.id };
  }

  async function deletePage() {
    if (livePageOrder.length <= 1) return;
    const doomed = pageId;
    // nearest survivor: previous if any, else next (livePageOrder still contains doomed here)
    const target = (livePageOrder[pageIndex - 1] ?? livePageOrder[pageIndex + 1])?.id;
    if (!target) return;
    await closeTextEditor();
    await engine?.commitSelection();
    await engine?.idle();
    await app.repo!.softDeletePage(doomed);
    app.showToast('Page deleted', async () => {
      await app.repo!.restorePage(doomed);
      app.route = { screen: 'editor', notebookId, pageId: doomed };
    });
    app.route = { screen: 'editor', notebookId, pageId: target };
  }

  async function back() {
    await closeTextEditor();
    await engine?.commitSelection();
    await engine?.idle();
    app.route = { screen: 'library' };
  }

  // ---- export (M4 spec §2.3): whole-notebook flattened PDF, client-side ----
  let exporting = $state(false);
  async function exportPdf() {
    if (!notebook || exporting || !engine) return;
    exporting = true;
    app.showToast('Exporting…');
    try {
      // Settle any open edit/selection so the export sees committed state.
      await closeTextEditor();
      await engine.commitSelection();
      await engine.idle();
      // Lazy chunk: pdf-lib must never enter the initial JS graph.
      const { exportNotebook } = await import('../export/exportPdf');
      const bytes = await exportNotebook(notebook, livePageOrder, {
        getActiveStrokes: (pid) => app.repo!.getActiveStrokes(pid),
        getActiveTextBoxes: (pid) => app.repo!.getActiveTextBoxes(pid),
        getFileBytes,
      });
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      // Strip characters that are illegal in filenames on common platforms.
      a.download = `${notebook.title.replace(/[\\/:*?"<>|]/g, '_') || 'notebook'}.pdf`;
      a.click();
      // Revoke later: the click only STARTS the download (iPad share sheet).
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      app.showToast(`Export failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      exporting = false;
    }
  }
</script>

<div class="editor">
  <Toolbar
    {engine} {writing} {canUndo} {canRedo} {historyEnabled} {historyMode}
    pageLabel={livePageOrder.length ? `${pageIndex + 1} / ${livePageOrder.length}` : ''}
    isInfinite={page?.kind === 'infinite'}
    candeletepage={livePageOrder.length > 1}
    onback={back}
    onprev={() => goToPage(pageIndex - 1)}
    onnext={() => goToPage(pageIndex + 1)}
    onaddpage={() => (showNewPage = true)}
    ondeletepage={deletePage}
    onpages={() => (showPages = !showPages)}
    onhistory={openHistory}
    ontoolchange={onToolChange}
    onexport={exportPdf}
    {exporting}
  />
  <div class="stage">
    <div class="canvas-host" bind:this={host}></div>
    <!-- Siblings of canvas-host (not children): the engine listens for pointer
         events on the host, and taps on the overlays must not reach it. -->
    {#if engine}
      <SelectionBar {engine} />
    {/if}
    {#if searchHighlight}
      <div class="search-highlights" class:fading={hlFading} aria-hidden="true">
        {#each searchHighlight as b, i (i)}
          <div
            class="search-highlight"
            style="left:{(b.r[0] - hlVp.x) * hlVp.scale}px; top:{(b.r[1] - hlVp.y) * hlVp.scale}px; width:{b.r[2] * hlVp.scale}px; height:{b.r[3] * hlVp.scale}px"
          ></div>
        {/each}
      </div>
    {/if}
    {#if engine && textEdit}
      <TextBoxEditor bind:this={textEditorRef} {engine} box={textEdit} onclose={() => (textEdit = null)} />
    {/if}
    {#if engine && historyMode}
      <HistoryBar {engine} onclose={() => (historyMode = false)} />
    {/if}
  </div>
  {#if debug}
    <DebugOverlay />
  {/if}
</div>

{#if showPages}
  <PagesDrawer
    pages={livePageOrder}
    currentPageId={pageId}
    theme={app.theme}
    repo={app.repo!}
    onpick={pickPage}
    onclose={() => (showPages = false)}
  />
{/if}

{#if showNewPage && notebook}
  <NewPageDialog
    defaultKind={notebook.defaultKind}
    defaultTemplate={notebook.defaultTemplate}
    oncreate={createPage}
    onclose={() => (showNewPage = false)}
  />
{/if}

<style>
  .editor { position: fixed; inset: 0; display: flex; flex-direction: column; }
  .stage { position: relative; flex: 1; }
  .canvas-host { position: absolute; inset: 0; overflow: hidden; background: var(--paper); }
  /* M5 search match highlights: pointer-transparent overlay above the canvas,
     below the floating bars; fades out via the parent's opacity transition. */
  .search-highlights { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 4; opacity: 1; transition: opacity 0.7s ease; }
  .search-highlights.fading { opacity: 0; }
  .search-highlight { position: absolute; background: var(--accent); opacity: 0.28; border-radius: 3px; outline: 2px solid var(--accent); }
</style>
